/**
 * lib/reports/renderReportSentences.ts — Template Sentences (deterministic)
 * ============================================================================
 * Converts a WeeklyReportPayload into the exact display sentences shown in
 * the UI. These are plain string templates — no LLM call happens here.
 * This is what guarantees every number the user sees is exactly what the
 * backend computed; the LLM (functions/) never generates these lines.
 */

import type { WeeklyReportPayload } from './buildWeeklyReportPayload';
import { formatRupees, formatPF, formatPct, formatNumber } from '@/lib/formatters';

/** brown = WoW mixed body; good/bad used for all-good / all-bad weeks and the impact line. */
export type ReportTone = 'good' | 'bad' | 'neutral' | 'brown';

export interface ImpactTrendLine {
  text: string;
  /** good = better ₹ outcome, bad = worse, brown = flat (same as overall mixed body). */
  tone: 'good' | 'bad' | 'brown';
}

export interface RenderedReport {
  facilityLines: { label: string; value: string }[];
  pf: { lines: string[]; tone: ReportTone };
  cd: { lines: string[]; tone: ReportTone };
  lf: { lines: string[]; tone: ReportTone };
  comparison: {
    lines: string[];
    tone: ReportTone;
    /** Deterministic net rebate/surcharge WoW line — green when better, red when worse. */
    impactTrend: ImpactTrendLine | null;
  };
  health: { title: string; explanation: string; action: string }[];
}

function impactPhrase(amount: number): string {
  if (amount > 0) return `a rebate of ${formatRupees(amount)}`;
  if (amount < 0) return `a surcharge of ${formatRupees(Math.abs(amount))}`;
  return 'no rebate or surcharge';
}

/**
 * Net PF+LF−CD impact week-over-week.
 * Higher algebraic value is always better (bigger rebate, or smaller surcharge).
 * The amounts themselves come from the billing sections — never invented here.
 */
function buildImpactTrendLine(lastWeekRs: number, thisWeekRs: number): ImpactTrendLine {
  const delta = thisWeekRs - lastWeekRs;
  const bothRebate = lastWeekRs > 0 && thisWeekRs > 0;
  const bothSurcharge = lastWeekRs < 0 && thisWeekRs < 0;
  const last = impactPhrase(lastWeekRs);
  const curr = impactPhrase(thisWeekRs);

  // Flat (or sub-paisa noise) — still show the amounts; colour follows overall body.
  if (Math.abs(delta) < 0.01) {
    return {
      tone: 'brown',
      text: `Last week you got ${last}; this week you got ${curr}.`,
    };
  }

  if (delta > 0) {
    if (bothSurcharge) {
      return {
        tone: 'good',
        text: `Last week you got ${last}; this week you got a smaller surcharge of ${formatRupees(Math.abs(thisWeekRs))}.`,
      };
    }
    if (bothRebate || thisWeekRs > 0) {
      return {
        tone: 'good',
        text: `Last week you got ${last}; this week you got a better rebate of ${formatRupees(thisWeekRs)}.`,
      };
    }
    return {
      tone: 'good',
      text: `Last week you got ${last}; this week you got ${curr} — an improvement.`,
    };
  }

  if (bothSurcharge || thisWeekRs < 0) {
    return {
      tone: 'bad',
      text: thisWeekRs < 0
        ? `Last week you got ${last}; this week you got a higher surcharge of ${formatRupees(Math.abs(thisWeekRs))}.`
        : `Last week you got ${last}; this week you got ${curr}.`,
    };
  }
  if (bothRebate || lastWeekRs > 0) {
    return {
      tone: 'bad',
      text: thisWeekRs > 0
        ? `Last week you got ${last}; this week you got a smaller rebate of ${formatRupees(thisWeekRs)}.`
        : `Last week you got ${last}; this week you got ${curr}.`,
    };
  }
  return {
    tone: 'bad',
    text: `Last week you got ${last}; this week you got ${curr} — weaker than last week.`,
  };
}

export function renderReportSentences(payload: WeeklyReportPayload): RenderedReport {
  const { facility, billing, healthFindings } = payload;

  const facilityLines = [
    { label: 'Consumer', value: facility.consumer },
    { label: 'DISCOM / Tariff', value: facility.discomTariff },
    { label: 'Contract Demand', value: `${facility.contractDemandKva} kVA` },
    { label: 'Tariff Rates', value: facility.tariffRatesLabel },
  ];

  // ── Power Factor ─────────────────────────────────────────────────────────
  const pfLines: string[] = [];
  let pfTone: 'good' | 'bad' | 'neutral' = 'neutral';
  if (!billing.pf.applicable) {
    pfLines.push(
      `Average power factor this week was ${formatPF(billing.pf.weekAvgPf)}, dropping to a low of ${formatPF(billing.pf.weekLowPf)}${billing.pf.weekLowPfDate ? ` on ${billing.pf.weekLowPfDate}` : ''}.`,
      `This tariff does not apply a per-period power factor rebate or surcharge table, so no rupee impact is shown for PF here.`,
    );
    pfTone = 'neutral';
  } else if (billing.pf.allGoodAllWeek) {
    pfLines.push(
      `Power factor stayed at or above the target band all week — average ${formatPF(billing.pf.weekAvgPf)}, lowest ${formatPF(billing.pf.weekLowPf)}.`,
      `Everything was fine — you saved ${formatRupees(billing.pf.actualImpactRs)} this week.`,
    );
    pfTone = 'good';
  } else {
    // Only mention the hypothetical "could have earned more" figure when the
    // actual outcome was a real cost (a surcharge) — if a rebate was already
    // earned (even partial), restating a bigger hypothetical number on top of
    // it just repeats the same subtraction the reader already did in their head.
    const pfIsSurcharge = billing.pf.actualImpactRs < 0;
    const dipIsMeaningful = billing.pf.dipIsMeaningful;
    pfLines.push(
      dipIsMeaningful
        ? `Average power factor this week was ${formatPF(billing.pf.weekAvgPf)}, dropping to a low of ${formatPF(billing.pf.weekLowPf)}${billing.pf.weekLowPfDate ? ` on ${billing.pf.weekLowPfDate}` : ''}. PF slipped below the tariff's rebate-eligible mark (${formatPF(billing.pf.thresholdUsed)}) on ${billing.pf.daysBelowThreshold} of ${billing.pf.daysSpan} days.`
        : `Power factor stayed steady around ${formatPF(billing.pf.weekAvgPf)} this week, ${billing.pf.daysBelowThreshold > 0 ? `dipping below the rebate-eligible mark (${formatPF(billing.pf.thresholdUsed)}) on ${billing.pf.daysBelowThreshold} of ${billing.pf.daysSpan} days` : `never dropping below the rebate-eligible mark (${formatPF(billing.pf.thresholdUsed)})`}.`,
      pfIsSurcharge
        ? `Had PF stayed at target, you would have earned a rebate of ${formatRupees(billing.pf.hypotheticalImpactRs)}. Instead, the dip cost you a net surcharge of ${formatRupees(Math.abs(billing.pf.actualImpactRs))}.`
        : billing.pf.actualImpactRs > 0
          // "still" only makes sense when there was some dip to overcome — with a
          // clean week, just state the rebate plainly, no implied contrast.
          ? billing.pf.daysBelowThreshold > 0
            ? `You still earned a rebate of ${formatRupees(billing.pf.actualImpactRs)} this week.`
            : `You earned a rebate of ${formatRupees(billing.pf.actualImpactRs)} this week.`
          : `No rebate or surcharge applied on power factor this week.`,
    );
    pfTone = pfIsSurcharge ? 'bad' : 'good';
  }

  // ── Contract Demand Utilization ──────────────────────────────────────────
  const cdLines: string[] = [];
  let cdTone: 'good' | 'bad' | 'neutral' = 'neutral';
  cdLines.push(
    `Averaged ${formatPct(billing.cd.avgUtilizationPct, 0)} utilisation (${formatNumber(billing.cd.avgUtilizationKva, 0)} kVA) this week and peaked at ${formatPct(billing.cd.peakUtilizationPct, 0)} (${formatNumber(billing.cd.peakUtilizationKva, 0)} kVA) of your ${billing.cd.contractDemandKva} kVA Contract Demand.`,
  );
  if (billing.cd.exceeded) {
    cdLines.push(`Peak demand exceeded your Contract Demand by ${formatNumber(billing.cd.excessKva, 0)} kVA, resulting in an estimated demand penalty of ${formatRupees(billing.cd.penaltyRs)}.`);
    cdTone = 'bad';
  } else if (billing.cd.underUtilized) {
    cdLines.push(`This is not a good sign — utilisation never crossed 80% of contract demand, meaning roughly ${formatNumber(billing.cd.unusedKvaAtPeak, 0)} kVA of contracted capacity went unused even at the week's peak.`);
    cdTone = 'bad';
  } else {
    cdLines.push(`Utilisation stayed within a healthy range relative to your Contract Demand this week.`);
    cdTone = 'good';
  }

  // ── Load Factor ───────────────────────────────────────────────────────────
  const lfLines: string[] = [];
  let lfTone: 'good' | 'bad' | 'neutral' = 'neutral';
  if (!billing.lf.applicable) {
    lfLines.push(
      `Average load factor this week was ${formatPct(billing.lf.weekAvgLfPct, 0)}, falling as low as ${formatPct(billing.lf.weekLowLfPct, 0)}${billing.lf.weekLowLfDate ? ` on ${billing.lf.weekLowLfDate}` : ''}.`,
      `This tariff does not apply a load factor rebate or surcharge, so no rupee impact is shown for load factor here.`,
    );
  } else {
    const lfIsSurcharge = billing.lf.actualImpactRs < 0;
    const nearFullUtilization = billing.lf.nearFullUtilization;
    const dipIsMeaningful = billing.lf.dipIsMeaningful;
    lfLines.push(
      nearFullUtilization
        ? `Load was utilized at close to its full potential this week.`
        : dipIsMeaningful
          ? `Averaged ${formatPct(billing.lf.weekAvgLfPct, 0)} this week, falling as low as ${formatPct(billing.lf.weekLowLfPct, 0)}${billing.lf.weekLowLfDate ? ` on ${billing.lf.weekLowLfDate}` : ''} — ${billing.lf.weekLowLfPct < billing.lf.rebateThresholdPct ? `below the ${formatPct(billing.lf.rebateThresholdPct, 0)} rebate threshold` : `within range of the ${formatPct(billing.lf.rebateThresholdPct, 0)} rebate threshold`}.`
          : `Load factor stayed steady around ${formatPct(billing.lf.weekAvgLfPct, 0)} this week — ${billing.lf.weekAvgLfPct < billing.lf.rebateThresholdPct ? `below the ${formatPct(billing.lf.rebateThresholdPct, 0)} rebate threshold` : `within range of the ${formatPct(billing.lf.rebateThresholdPct, 0)} rebate threshold`}.`,
      // Same rule as PF: only surface the hypothetical bigger saving when the
      // actual outcome was a real surcharge — a rebate already earned (or the
      // full one, when allGoodAllWeek) doesn't need a "could've saved more" line.
      lfIsSurcharge
        ? `Had it been maintained at target, you would have saved ~${formatRupees(billing.lf.hypotheticalImpactRs)}. Instead, this resulted in a ~${formatRupees(Math.abs(billing.lf.actualImpactRs))} surcharge this week.`
        : billing.lf.actualImpactRs > 0
          ? `This earned you a saving of ${formatRupees(billing.lf.actualImpactRs)} this week.`
          : `No rebate or surcharge applied on load factor this week.`,
    );
    lfTone = lfIsSurcharge ? 'bad' : 'good';
  }

  // ── Week comparison ───────────────────────────────────────────────────────
  // Body tone follows overall metric result (green / red / brown). A separate
  // impact-trend line (green/red) carries the net ₹ rebate/surcharge amounts
  // from the three billing sections above.
  const cmp = billing.comparison;
  let comparisonLines: string[];
  let cmpTone: ReportTone;
  let impactTrend: ImpactTrendLine | null = null;
  if (!cmp.hasLastWeekData) {
    // No telemetry at all for the prior week — showing "from 0% to X%" would
    // fabricate a jump out of missing data, not a real change. Say so plainly.
    comparisonLines = [`No data is available for the prior week yet, so a week-over-week comparison isn't possible — this will appear starting next week.`];
    cmpTone = 'neutral';
  } else {
    const pfDir = cmp.pfDeltaPts > 0 ? 'improved' : cmp.pfDeltaPts < 0 ? 'worsened' : 'stayed flat';
    const lfDir = cmp.lfDeltaPts > 0 ? 'improved' : cmp.lfDeltaPts < 0 ? 'worsened' : 'stayed flat';
    const cdDir = cmp.cdUtilDeltaPts > 0 ? 'increased' : cmp.cdUtilDeltaPts < 0 ? 'decreased' : 'stayed flat';
    comparisonLines = [
      `Compared to the prior week: power factor ${pfDir}, going from ${formatPF(cmp.lastWeekPf)} to ${formatPF(cmp.thisWeekPf)}; load factor ${lfDir}, going from ${formatPct(cmp.lastWeekLfPct, 0)} to ${formatPct(cmp.thisWeekLfPct, 0)}; and CD utilisation ${cdDir}, going from ${formatPct(cmp.lastWeekCdUtilPct, 0)} to ${formatPct(cmp.thisWeekCdUtilPct, 0)}.`,
      cmp.overallLabel === 'improved'
        ? `Overall, this week's performance was better than last week.`
        : cmp.overallLabel === 'declined'
          ? `Overall, this week's performance was weaker than last week.`
          : `Overall, this week's performance was mixed compared to last week.`,
    ];
    cmpTone =
      cmp.overallLabel === 'improved' ? 'good'
        : cmp.overallLabel === 'declined' ? 'bad'
          : 'brown';
    if (cmp.hasRebateSurchargeImpact) {
      impactTrend = buildImpactTrendLine(cmp.lastWeekTotalImpactRs, cmp.thisWeekTotalImpactRs);
      // Flat ₹ outcome: keep the amounts line, but match the section body colour
      // (green / red / brown) instead of forcing brown.
      if (impactTrend.tone === 'brown') {
        impactTrend = { ...impactTrend, tone: cmpTone };
      }
    }
  }

  return {
    facilityLines,
    pf: { lines: pfLines, tone: pfTone },
    cd: { lines: cdLines, tone: cdTone },
    lf: { lines: lfLines, tone: lfTone },
    comparison: { lines: comparisonLines, tone: cmpTone, impactTrend },
    health: healthFindings.map(h => ({ title: h.title, explanation: h.explanation, action: h.action })),
  };
}
